/**
 * `TeamOfAgentsPane` — the first slot inside `RightSidebar`. Renders:
 *
 *   1. `AwaitingUserBanner` (when awaiting-user count > 0).
 *   2. TWO TABS (operator directive 2026-07-19). The former single list
 *      appended standalone harnesses BELOW the missions behind a divider,
 *      so on a busy case they sat off-screen and were never seen. Now:
 *        - "Agentes"  — missions (the DEFAULT tab), and
 *        - "Tarefas"  — standalone harness runs + scheduler runs.
 *      Each tab header carries an ACTIVITY INDICATOR (a spinner + count)
 *      when the tab the lawyer is NOT looking at has work in flight, so
 *      hidden activity announces itself instead of hiding.
 *   3. Empty state when nothing's running (per tab).
 *   4. Error chip on poll failure (fail-soft).
 *   5. Footer with the last-polled-at timestamp + a refresh button.
 *
 * §I.2 auto-expand: an effect watches `lastPolledAt` going from null
 * → non-null and calls `setRightSidebarExpandedAuto(true)` if
 * `selectShouldAutoExpand` is true at that moment. The auto setter
 * short-circuits if the lawyer has already manually toggled the
 * sidebar this session (`lastUserToggleAt !== null`), so the user
 * preference always wins.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useShallow } from "zustand/react/shallow";
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Pause,
  RefreshCw,
} from "lucide-react";
import { AgentsIcon, TasksIcon } from "@/lib/monitor-icons";
import { cn } from "@/lib/utils";
import { useMissionsPoll } from "@/hooks/useMissionsPoll";
import { useHarnTasksPoll } from "@/hooks/useHarnTasksPoll";
import {
  selectAwaitingUserCount,
  selectMissionsByCase,
  selectShouldAutoExpand,
  useMissionsStore,
} from "@/stores/useMissionsStore";
import {
  selectHarnShouldAutoExpand,
  selectTasksByCase,
  useHarnTasksStore,
} from "@/stores/useHarnTasksStore";
import { useUIStore } from "@/stores/useUIStore";
import { useScheduledRunsPoll } from "@/hooks/useScheduledRunsPoll";
import { useCases } from "@/hooks/useCases";
import { buildCaseLabelIndex, resolveCaseLabel } from "./case-label";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { AwaitingUserBanner } from "./AwaitingUserBanner";
import { MissionRow } from "./MissionRow";
import { HarnessRow } from "./HarnessRow";
import { ScheduledRunRow } from "./ScheduledRunRow";
import {
  buildMonitorTree,
  partitionQueuedMissions,
  isNodeActive,
  isNodeAwaitingUser,
  nodeLastActivityAt,
  partitionInfrastructure,
  splitTree,
} from "./monitor-tree";
import type { MissionTreeNode } from "./monitor-tree";
import { isOnboardingActive } from "@/lib/setup-wizard";

export function TeamOfAgentsPane() {
  const { t } = useTranslation();

  // Cross-case polling for the cross-mode pane.
  const { cancelMission, refetch: refetchMissions } = useMissionsPoll({
    caseId: null,
  });
  const { cancelTask, refetch: refetchTasks } = useHarnTasksPoll({
    caseId: null,
  });

  // Re-render only when the underlying Maps mutate (useShallow gives
  // stable references unless contents change).
  const missions = useMissionsStore(
    useShallow((s) => selectMissionsByCase(s)),
  );
  const tasks = useHarnTasksStore(useShallow((s) => selectTasksByCase(s)));
  const awaitingUserCount = useMissionsStore((s) => selectAwaitingUserCount(s));
  // Earliest of the two pollers' lastPolledAt — controls when the
  // first-poll auto-expand effect can fire (BOTH must have polled
  // at least once OR one must be non-null + the other still null
  // for a brief window). Using EITHER non-null is reasonable: the
  // auto-expand looks at the union of both stores via the
  // discriminator selectors below.
  const missionsLastPolledAt = useMissionsStore((s) => s.lastPolledAt);
  const tasksLastPolledAt = useHarnTasksStore((s) => s.lastPolledAt);
  const lastPolledAt = chooseLater(missionsLastPolledAt, tasksLastPolledAt);
  const missionsPollError = useMissionsStore((s) => s.pollError);
  const tasksPollError = useHarnTasksStore((s) => s.pollError);
  const pollError = missionsPollError ?? tasksPollError;

  // Scheduler-started reminder/system runs (recjobs §3.5(b)): pure cron
  // turns that never create a harness task, merged in from the execution
  // log so scheduler work is not invisible here.
  const { runs: scheduledRuns } = useScheduledRunsPoll(null);

  // Case labels for the cross-case rows. `useCases` is the shared
  // TanStack query (`["cases"]`, 60s stale) the shell already fetches at
  // boot, so this resolves names off the existing cache rather than
  // issuing a request of its own.
  const { data: caseList } = useCases();
  const caseNames = useMemo(() => buildCaseLabelIndex(caseList), [caseList]);
  // Distinguishes "no such case" from "the registry has not arrived yet".
  const caseRegistryLoaded = caseNames.size > 0;
  const activeCaseId = useActiveContextStore(
    (s) => s.selection?.caseId ?? null,
  );

  // Build + split in ONE memo: `splitTree` returns fresh arrays every
  // call, so splitting outside would give any downstream memo new
  // dependency identities on every render and defeat it.
  const { missionNodes, queuedNodes, taskNodes } = useMemo(() => {
    const tree = buildMonitorTree(missions, tasks, scheduledRuns);
    const split = splitTree(tree);
    // The admission queue is pulled OUT of the main list and rendered as its
    // own collapsed group — see `partitionQueuedMissions`.
    const { queued, live } = partitionQueuedMissions(split.missions);
    return {
      missionNodes: live,
      queuedNodes: queued,
      // Tab 2 holds every single-shot background execution — standalone
      // harness runs and scheduler runs alike — re-merged latest-first.
      taskNodes: [...split.standalones, ...split.scheduledRuns].sort(
        (a, b) => nodeLastActivityAt(b) - nodeLastActivityAt(a),
      ),
    };
  }, [missions, tasks, scheduledRuns]);

  // Infrastructure runs (checkpoints, reconcilers) are the platform's
  // plumbing, not the lawyer's work — they are hidden by default and the tab
  // count follows the filter. Not cosmetic: the count is the headline answer
  // to "how busy are my agents", and it read 289 on a demo runtime, almost
  // entirely `case-checkpoint`. Every node keeps flowing through
  // `partitionInfrastructure`, so the toggle reveals rather than refetches.
  const [showSystemActivity, setShowSystemActivity] = useState(false);
  const { userWork: userTaskNodes, infrastructure: infraTaskNodes } = useMemo(
    () => partitionInfrastructure(taskNodes),
    [taskNodes],
  );
  const visibleTaskNodes = showSystemActivity ? taskNodes : userTaskNodes;

  const [activeTab, setActiveTab] = useState<PaneTab>("agents");
  // Deliberately over the LIVE nodes only, so a queued mission does NOT spin
  // the tab. `isNodeActive` counts `queued` as active (it drives the fast
  // poll cadence, which we still want so the queue visibly advances), but the
  // tab spinner means "work is running here" — a mission parked behind the
  // admission gate is precisely what is NOT running, and the "NA FILA (n)"
  // group plus the tab count already say it is there.
  const agentsActiveCount = missionNodes.filter(isNodeActive).length;
  const tasksActiveCount = userTaskNodes.filter(isNodeActive).length;
  const agentsAwaitingCount = missionNodes.filter(isNodeAwaitingUser).length;
  // Awaiting-user is counted over ALL tasks including infrastructure: a run
  // blocked on the lawyer must never be hidden by a display filter. Nothing
  // ships infra + awaiting_user today, but the banner is a safety surface and
  // it should not depend on that staying true.
  const tasksAwaitingCount = taskNodes.filter(isNodeAwaitingUser).length;

  // ─── §I.2 auto-expand effect ───────────────────────────────────────
  // Fires ONCE per mount, after either poller's first poll resolves.
  // The auto setter on useUIStore short-circuits internally when the
  // user has already toggled this session.
  const autoExpandFiredRef = useRef<boolean>(false);
  const setRightSidebarExpandedAuto = useUIStore(
    (s) => s.setRightSidebarExpandedAuto,
  );
  useEffect(() => {
    if (autoExpandFiredRef.current) return;
    if (lastPolledAt === null) return;
    // NEVER auto-expand during first-run setup. A user who has not finished
    // configuring their assistant has no context for "Agentes Lexiun", and
    // the pane competes for attention with the one thing they are supposed
    // to be doing — the operator's report of the onboarding screen named the
    // open sidebar as part of what made it confusing. Deliberately NOT
    // consuming the fired-ref: once setup is done, the next mount may expand
    // normally, so this suppresses rather than permanently disables.
    if (isOnboardingActive()) return;
    autoExpandFiredRef.current = true;
    const shouldExpand =
      selectShouldAutoExpand(useMissionsStore.getState()) ||
      selectHarnShouldAutoExpand(useHarnTasksStore.getState());
    if (shouldExpand) {
      setRightSidebarExpandedAuto(true);
    }
  }, [lastPolledAt, setRightSidebarExpandedAuto]);

  const refetchBoth = async () => {
    await Promise.all([refetchMissions(), refetchTasks()]);
  };

  return (
    <div className="flex h-full flex-col" data-testid="team-of-agents-pane">
      <AwaitingUserBanner count={awaitingUserCount} />
      <PaneTabs
        active={activeTab}
        onSelect={setActiveTab}
        agentsCount={missionNodes.length + queuedNodes.length}
        tasksCount={userTaskNodes.length}
        agentsActiveCount={agentsActiveCount}
        tasksActiveCount={tasksActiveCount}
        agentsAwaitingCount={agentsAwaitingCount}
        tasksAwaitingCount={tasksAwaitingCount}
      />
      <div className="flex-1 overflow-y-auto">
        {pollError && (
          <div
            data-testid="team-of-agents-error"
            role="alert"
            // The raw reason stays REACHABLE for support but never becomes the
            // lawyer's sentence. Same split MissionErrorBlock already uses: a
            // friendly localized line, the machine detail demoted.
            title={pollError}
            className="m-2 rounded border border-red-200/60 bg-red-50 px-2 py-1.5 text-[11px] text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300"
          >
            {/* ⚠ Do NOT interpolate `pollError` back into this sentence, and do
                NOT restore a copy that names a CAUSE. The old string asserted
                one — "Não foi possível alcançar o serviço de agentes —
                <raw>" — for EVERY poll failure, and this store's own docblock
                (useMissionsStore.ts:310-323) records what that costs: a
                QuotaExceededError (a full disk) was reported as an unreachable
                backend, and "operators went looking at the network for a purely
                client-side failure". The quota CAUSE was fixed; the asserting
                SHAPE outlived it, and tester-galvao then caught it narrating an
                EXPECTED product-initiated restart as a breakage (2026-09-13).
                A poll failure is not evidence of WHY, so the copy states only
                what is known: the panel did not refresh, and it is retrying. */}
            {t("rightSidebar.pollError")}
          </div>
        )}
        {activeTab === "agents" ? (
          missionNodes.length === 0 && queuedNodes.length === 0 ? (
            <EmptyState testId="team-of-agents-empty" hintKey="rightSidebar.empty.hint" />
          ) : (
            <>
              <QueueSection
                nodes={queuedNodes}
                onCancel={cancelMission}
                caseNames={caseNames}
                activeCaseId={activeCaseId}
                caseRegistryLoaded={caseRegistryLoaded}
              />
              <ul
                data-testid="team-of-agents-list"
                className="divide-cream-200/40 dark:divide-coffee-800/40"
              >
                {missionNodes.map((node) => (
                  <MissionRow
                    key={node.missionId}
                    node={node}
                    onCancel={cancelMission}
                    caseLabel={resolveCaseLabel(node.caseId, caseNames)}
                    activeCaseId={activeCaseId}
                    caseRegistryLoaded={caseRegistryLoaded}
                  />
                ))}
              </ul>
            </>
          )
        ) : visibleTaskNodes.length === 0 ? (
          <EmptyState
            testId="team-of-agents-tasks-empty"
            hintKey="rightSidebar.empty.tasksHint"
          />
        ) : (
          <ul
            data-testid="team-of-agents-tasks-list"
            className="divide-cream-200/40 dark:divide-coffee-800/40"
          >
            {visibleTaskNodes.map((node) =>
              node.kind === "scheduled-run" ? (
                <ScheduledRunRow
                  key={node.runId}
                  node={node}
                  caseLabel={resolveCaseLabel(node.caseId, caseNames)}
                  activeCaseId={activeCaseId}
                  caseRegistryLoaded={caseRegistryLoaded}
                />
              ) : (
                <HarnessRow
                  key={node.taskId}
                  node={node}
                  onCancel={cancelTask}
                  caseLabel={resolveCaseLabel(node.caseId, caseNames)}
                  activeCaseId={activeCaseId}
                  caseRegistryLoaded={caseRegistryLoaded}
                />
              ),
            )}
          </ul>
        )}
        {/* Rendered for the tasks tab in EVERY state, empty included: an empty
            list while N infra rows sit behind the filter would be a claim that
            is false in the reassuring direction. The toggle is what makes the
            hiding discoverable, and it self-hides when there is nothing to
            reveal. */}
        {activeTab === "tasks" && (
          <SystemActivityToggle
            count={infraTaskNodes.length}
            shown={showSystemActivity}
            onToggle={() => setShowSystemActivity((v) => !v)}
            t={t}
          />
        )}
      </div>
      <PaneFooter lastPolledAt={lastPolledAt} onRefetch={refetchBoth} />
    </div>
  );
}

/**
 * "Mostrar atividade do sistema" — the discoverability half of the filter.
 *
 * Renders NOTHING when there is no infrastructure to reveal, so a lawyer
 * whose case has never run a checkpoint is not offered a control over an
 * empty set. When there IS something hidden it always shows, including under
 * the empty state, because "no tasks" while rows sit behind a filter is a
 * claim that is false in the reassuring direction.
 */
function SystemActivityToggle({
  count,
  shown,
  onToggle,
  t,
}: {
  count: number;
  shown: boolean;
  onToggle: () => void;
  t: TFunction;
}) {
  if (count === 0) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid="team-of-agents-system-activity-toggle"
      aria-pressed={shown}
      className="flex w-full items-center gap-1.5 px-3 py-2 text-[11px] text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
    >
      {shown ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      <span>
        {shown
          ? t("rightSidebar.systemActivity.hide", { count })
          : t("rightSidebar.systemActivity.show", { count })}
      </span>
    </button>
  );
}

/**
 * The ADMISSION QUEUE group — collapsed by default, pinned above the live
 * list (operator direction 2026-07-21).
 *
 * Missions can be gated by design (per-case 1, per-user 5), and a gated one
 * used to sit inline among running rows wearing a grey dot, reading as
 * "about to start". Here the count carries the depth at a glance, opening it
 * shows the FIFO order the gate will actually release them in, and it
 * renders nothing at all when the queue is empty — which is most of the time.
 */
function QueueSection({
  nodes,
  onCancel,
  caseNames,
  activeCaseId,
  caseRegistryLoaded,
}: {
  nodes: MissionTreeNode[];
  onCancel: (missionId: string) => Promise<void>;
  caseNames: Map<string, string>;
  activeCaseId: string | null;
  caseRegistryLoaded: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (nodes.length === 0) return null;

  return (
    <div
      data-testid="team-of-agents-queue"
      // PINNED to the top of the scroll container: the queue is structurally
      // always the first block (it is partitioned out of the sorted list, so
      // no newly-arriving mission can ever displace it), and `sticky` keeps
      // it visible while the list below scrolls. Needs an opaque background
      // matching the sidebar, or rows would scroll through it.
      className="sticky top-0 z-10 border-b border-cream-200/60 bg-white dark:border-coffee-800/60 dark:bg-coffee-950"
    >
      <button
        type="button"
        data-testid="team-of-agents-queue-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold tracking-wide text-yellow-500 hover:text-yellow-400"
      >
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        <Clock size={11} aria-hidden />
        {t("rightSidebar.queue.heading", { count: nodes.length })}
      </button>
      {open && (
        <ul
          data-testid="team-of-agents-queue-list"
          className="divide-cream-200/40 dark:divide-coffee-800/40"
        >
          {nodes.map((node) => (
            <MissionRow
              key={node.missionId}
              node={node}
              onCancel={onCancel}
              caseLabel={resolveCaseLabel(node.caseId, caseNames)}
              activeCaseId={activeCaseId}
              caseRegistryLoaded={caseRegistryLoaded}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

export type PaneTab = "agents" | "tasks";

/** The tab bar. Its job beyond navigation is the ACTIVITY INDICATOR: a
 *  spinner on the INACTIVE tab when work is running there, because the
 *  whole reason for splitting the list was that the hidden half went
 *  unnoticed. The count is shown on both tabs so the lawyer can see there
 *  is something to switch to even when nothing is currently running. */
function PaneTabs({
  active,
  onSelect,
  agentsCount,
  tasksCount,
  agentsActiveCount,
  tasksActiveCount,
  agentsAwaitingCount,
  tasksAwaitingCount,
}: {
  active: PaneTab;
  onSelect: (tab: PaneTab) => void;
  agentsCount: number;
  tasksCount: number;
  agentsActiveCount: number;
  tasksActiveCount: number;
  agentsAwaitingCount: number;
  tasksAwaitingCount: number;
}) {
  const { t } = useTranslation();
  const tabs: Array<{
    id: PaneTab;
    label: string;
    count: number;
    Icon: typeof AgentsIcon;
    activeCount: number;
    awaitingCount: number;
  }> = [
    {
      id: "agents",
      label: t("rightSidebar.tabs.agents"),
      count: agentsCount,
      Icon: AgentsIcon,
      activeCount: agentsActiveCount,
      awaitingCount: agentsAwaitingCount,
    },
    {
      id: "tasks",
      label: t("rightSidebar.tabs.tasks"),
      count: tasksCount,
      Icon: TasksIcon,
      activeCount: tasksActiveCount,
      awaitingCount: tasksAwaitingCount,
    },
  ];
  return (
    <div
      role="tablist"
      data-testid="team-of-agents-tabs"
      className="flex shrink-0 border-b border-cream-200/60 dark:border-coffee-800/60"
    >
      {tabs.map((tab) => {
        const selected = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            data-testid={`team-of-agents-tab-${tab.id}`}
            data-active-count={tab.activeCount}
            data-awaiting-count={tab.awaitingCount}
            title={
              tab.awaitingCount > 0
                ? t("rightSidebar.tabs.awaitingTitle", {
                    count: tab.awaitingCount,
                  })
                : tab.activeCount > 0
                  ? t("rightSidebar.tabs.runningTitle", { count: tab.activeCount })
                  : tab.label
            }
            onClick={() => onSelect(tab.id)}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 px-2 py-1.5 text-[11px] transition-colors",
              selected
                ? "border-b-2 border-blue-500/70 font-medium text-slate-700 dark:text-slate-200"
                : "border-b-2 border-transparent font-normal text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300",
            )}
          >
            <tab.Icon size={13} aria-hidden className="shrink-0" />
            <span>{tab.label}</span>
            {tab.count > 0 && (
              <span className="tabular-nums text-[10px] text-slate-400 dark:text-slate-500">
                {tab.count}
              </span>
            )}
            {/* The indicator that makes the hidden tab discoverable. PARKED
                outranks "running": a spinner says "in progress, nothing needed
                here", which is the opposite of the truth for a run that moves
                only when a reply arrives.
                ⚠ The tooltip says "aguardando retorno", NOT "aguardando você" —
                `isNodeAwaitingUser` cannot tell a human gate from an agent park
                (⚖ F6; see its docblock), so attributing the wait to the lawyer
                would be a claim this surface has no basis for. */}
            {tab.awaitingCount > 0 ? (
              <Pause
                data-testid={`team-of-agents-tab-awaiting-${tab.id}`}
                size={11}
                className="text-amber-500"
                aria-label={t("rightSidebar.tabs.awaitingTitle", {
                  count: tab.awaitingCount,
                })}
              />
            ) : (
              tab.activeCount > 0 && (
                <Loader2
                  data-testid={`team-of-agents-tab-spinner-${tab.id}`}
                  size={11}
                  className="animate-spin text-blue-500"
                  aria-label={t("rightSidebar.tabs.runningTitle", {
                    count: tab.activeCount,
                  })}
                />
              )
            )}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({
  testId,
  hintKey,
}: {
  testId: string;
  hintKey: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={testId}
      className="flex flex-1 flex-col items-center justify-center gap-1 p-6 text-center text-[11px] text-slate-400 dark:text-slate-500"
    >
      <p className="text-sm">🤖</p>
      <p>{t("rightSidebar.empty.title")}</p>
      <p className="text-[10px] leading-tight">{t(hintKey)}</p>
    </div>
  );
}

function PaneFooter({
  lastPolledAt,
  onRefetch,
}: {
  lastPolledAt: number | null;
  onRefetch: () => Promise<void>;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="team-of-agents-footer"
      className="flex items-center justify-between border-t border-cream-200/60 px-3 py-1.5 text-[10px] text-slate-400 dark:border-coffee-800/60 dark:text-slate-500"
    >
      <span title={lastPolledAt ? new Date(lastPolledAt).toISOString() : ""}>
        {lastPolledAt
          ? t("rightSidebar.lastPolledAt", { time: formatRelative(t, lastPolledAt) })
          : t("rightSidebar.notYetPolled")}
      </span>
      <button
        type="button"
        data-testid="team-of-agents-refresh"
        title={t("rightSidebar.refreshAriaLabel")}
        aria-label={t("rightSidebar.refreshAriaLabel")}
        onClick={() => {
          void onRefetch();
        }}
        className={cn(
          "flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-cream-100 hover:text-slate-600 dark:hover:bg-coffee-800 dark:hover:text-slate-300",
        )}
      >
        <RefreshCw size={11} />
      </button>
    </div>
  );
}

/** Returns the later of two nullable timestamps. Used to derive the
 *  pane's overall "last poll" indicator from the two pollers' values. */
function chooseLater(
  a: number | null,
  b: number | null,
): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return a > b ? a : b;
}

/** Localized "last poll" fragment. Was hardcoded English ("just now",
 *  "5m ago") interpolated into a pt-BR label — so the footer read
 *  "última verificação: just now". Now every fragment goes through i18n. */
function formatRelative(t: TFunction, ts: number): string {
  const delta = Date.now() - ts;
  if (delta < 5_000) return t("rightSidebar.relativeTime.justNow");
  if (delta < 60_000)
    return t("rightSidebar.relativeTime.secondsAgo", { n: Math.floor(delta / 1000) });
  if (delta < 3_600_000)
    return t("rightSidebar.relativeTime.minutesAgo", { n: Math.floor(delta / 60_000) });
  if (delta < 86_400_000)
    return t("rightSidebar.relativeTime.hoursAgo", { n: Math.floor(delta / 3_600_000) });
  return t("rightSidebar.relativeTime.daysAgo", { n: Math.floor(delta / 86_400_000) });
}
